import React, { useCallback, useMemo, useState } from 'react';
import { App } from 'antd';
import { useNavigate } from 'react-router-dom';

import CombinedScopeSwitch, { SCOPE_COMBINED, SCOPE_COLLECTION } from '../components/combinedLists/CombinedScopeSwitch';
import CombinedListSuggestion from '../components/combinedLists/CombinedListSuggestion';
import { resolveCombinedList } from '../services/combinedLists';
import {
	cardPlural,
	carryFilterAcross,
	combinedListPath,
	combinedTargetFor,
	newCombinedListHref,
} from '../utils/combinedLists';
import useCombinedListMatches from './useCombinedListMatches';

/**
 * A collection List's way into the combined list of its card (KPR-1022): the
 * scope switch "This collection · All my <Card plural>" and the one-time
 * suggestion. Both appear only where the List shows ONE card and two or more
 * of the person's collections hold it — the person saved a list on that card
 * (the switch opens it), or keepr suggests one (an unsaved list).
 *
 *   const { scopeSwitch, suggestion } = useCombinedScope({ card, elements, collectionId, query });
 *
 * Switching carries the search AND the filter (Zach, 2026-10-20): each
 * condition is rewritten from the collection's element ids to the list's
 * tokens by element name (`carryFilterAcross`), so the list's vocabulary is
 * read first (`resolve`). What cannot be lined up is left behind and handed to
 * the combined list page (`droppedConditions` in the navigation state), which
 * says so. The way back uses the source's own filter, exact.
 */
export default function useCombinedScope({ card = null, elements = [], collectionId = null, query = '', view = null, enabled = true } = {}) {
	const navigate = useNavigate();
	const { message } = App.useApp();
	const { lists, suggestions } = useCombinedListMatches({ enabled: enabled && Boolean(card) });
	const [busy, setBusy] = useState(false);

	const target = useMemo(
		() => (enabled && card ? combinedTargetFor(card, { lists, suggestions }) : null),
		[enabled, card, lists, suggestions]
	);
	const plural = card ? cardPlural(card) : 'items';

	const open = useCallback(async () => {
		if (!target || busy) return;
		const definition = target.kind === 'saved'
			? { match: target.list.match, exclude: target.list.exclude || [], include: target.list.include || [] }
			: { match: target.match };
		setBusy(true);
		let carried = { q: '', dropped: [] };
		if (`${query || ''}`.trim()) {
			try {
				const { vocabulary } = await resolveCombinedList(definition);
				carried = carryFilterAcross(query, { elements, vocabulary });
			} catch {
				carried = { q: '', dropped: ['Your filter'] };
			}
		}
		setBusy(false);
		const state = carried.dropped.length ? { droppedConditions: carried.dropped } : undefined;
		if (target.kind === 'saved') {
			const params = new URLSearchParams();
			// A List with no filter opens the list under its own stored one.
			if (`${query || ''}`.trim()) params.set('q', carried.q);
			if (view && view !== 'grid') params.set('view', view);
			if (collectionId) params.set('from', collectionId);
			navigate(`${combinedListPath(target.list._id)}?${params.toString()}`, { state });
			return;
		}
		const href = newCombinedListHref(definition, { q: carried.q || undefined, view: view && view !== 'grid' ? view : undefined, from: collectionId || undefined });
		if (!href.includes('def=')) { message.error('This list could not be opened.'); return; }
		navigate(href, { state });
	}, [target, busy, query, elements, view, collectionId, navigate, message]);

	const scopeSwitch = target ? (
		<CombinedScopeSwitch
			value={SCOPE_COLLECTION}
			plural={plural}
			disabled={busy}
			onChange={(scope) => { if (scope === SCOPE_COMBINED) open(); }}
		/>
	) : null;

	const suggestion = target && target.kind === 'suggested' ? (
		<CombinedListSuggestion suggestion={target.suggestion} plural={plural} onOpen={open} className="items-page__alert" />
	) : null;

	return { target, scopeSwitch, suggestion, open, busy };
}
